import { useState, useEffect, useCallback, useRef } from 'react'

/**
 * useContainerSize — observes an element's dimensions via ResizeObserver.
 *
 * @param {React.RefObject} elementRef - Ref to the DOM element to observe.
 * @param {Object} [options]
 * @param {number} [options.debounceSpan=0] - Debounce delay in ms. 0 means no debounce (default).
 * @returns {{width: number, height: number}}
 *
 * ResizeObserver already batches notifications per animation frame, so debouncing
 * is unnecessary for most cases. Use debounceSpan only if the consuming component
 * does expensive work on every resize (e.g. complex layout recalculations).
 *
 * ⚠️  STABLE REF REQUIREMENT — read before using:
 *
 * The internal effect captures elementRef.current exactly once on mount and never
 * re-runs, because all of its dependencies are permanently stable.
 * Mutating ref.current is invisible to React's effect system by design,
 * so the hook has no way to detect that the observed element has changed.
 *
 * This means the ref MUST point to a DOM element that:
 *   1. Exists on the component's first render (is not conditionally rendered).
 *   2. Remains the same DOM node for the component's entire lifetime.
 *
 * If the ref is placed on a conditionally rendered element, the element may not
 * exist when the effect runs, the observer will never be created, and resize events
 * will never be tracked — silently producing a size that is stuck at { width: 0, height: 0 }.
 *
 * ✅ Correct pattern: attach the ref to the nearest ancestor that is always in the DOM.
 * ❌ Wrong pattern:   attach the ref to an element inside a ternary, &&, or any
 *                     block that may not render on mount.
 */
function useContainerSize(elementRef, { debounceSpan = 0 } = {}) {
  const [ size, setSize ] = useState({ width: 0, height: 0 })
  const timerRef = useRef(null)

  const updateSize = useCallback((width, height) => {
    setSize((prev) => {
      if (prev.width === width && prev.height === height) return prev
      return { width, height }
    })
  }, [])

  useEffect(() => {
    const element = elementRef.current
    if (!element) {
      return
    }

    const { clientWidth, clientHeight } = element
    updateSize(clientWidth, clientHeight)

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect

      if (debounceSpan > 0) {
        clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => updateSize(width, height), debounceSpan)
      } else {
        updateSize(width, height)
      }
    })

    observer.observe(element)

    return () => {
      observer.disconnect()
      clearTimeout(timerRef.current)
    }
  }, [ elementRef, debounceSpan, updateSize ])

  return { ...size }
}

export default useContainerSize
